/**
 * Connection Status Indicator - Shows online/offline status and pending sync count
 */
import React from 'react';
import { useOffline } from '../../contexts/OfflineContext';
import { cn } from '../../lib/utils';
import { Badge } from './badge';
import { Button } from './button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './popover';
import {
  Wifi,
  WifiOff,
  Cloud,
  CloudOff,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Clock,
} from 'lucide-react';

export function ConnectionIndicator({ variant = 'default', showLabel = false }) {
  const { 
    isOnline, 
    isSyncing, 
    pendingCount, 
    syncProgress,
    lastSyncResult,
    triggerSync,
  } = useOffline();

  // Simple badge variant
  if (variant === 'badge') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={cn(
              'flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-colors',
              isOnline 
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
            )}>
              {isOnline ? (
                <Wifi className="h-3 w-3" />
              ) : (
                <WifiOff className="h-3 w-3" />
              )}
              {showLabel && (isOnline ? 'Online' : 'Offline')}
              {pendingCount > 0 && (
                <Badge variant="secondary" className="h-4 min-w-4 p-0 flex items-center justify-center text-[10px]">
                  {pendingCount}
                </Badge>
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>{isOnline ? 'Conectado' : 'Sem conexão'}</p>
            {pendingCount > 0 && (
              <p className="text-xs text-muted-foreground">
                {pendingCount} item(s) pendente(s)
              </p>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Full popover variant (default)
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className={cn(
            'gap-2 h-8',
            !isOnline && 'text-destructive',
            isSyncing && 'text-blue-600'
          )}
        >
          {isSyncing ? (
            <RefreshCw className="h-4 w-4 animate-spin" />
          ) : isOnline ? (
            <Cloud className="h-4 w-4 text-green-600" />
          ) : (
            <CloudOff className="h-4 w-4" />
          )}
          
          {pendingCount > 0 && (
            <Badge 
              variant={isOnline ? 'secondary' : 'destructive'} 
              className="h-5 px-1.5 text-xs"
            >
              {pendingCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      
      <PopoverContent className="w-72" align="end">
        <div className="space-y-3">
          {/* Connection Status */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isOnline ? (
                <>
                  <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                  <span className="font-medium">Online</span>
                </>
              ) : (
                <>
                  <div className="h-2 w-2 rounded-full bg-red-500" />
                  <span className="font-medium text-destructive">Offline</span>
                </>
              )}
            </div>
            
            {isOnline && pendingCount > 0 && (
              <Button 
                size="sm" 
                variant="outline" 
                onClick={triggerSync}
                disabled={isSyncing}
                className="h-7 text-xs"
              >
                {isSyncing ? (
                  <>
                    <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                    Sincronizando
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Sincronizar
                  </>
                )}
              </Button>
            )}
          </div>

          {/* Sync Progress */}
          {isSyncing && syncProgress && (
            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Progresso</span>
                <span>{syncProgress.current}/{syncProgress.total}</span>
              </div>
              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${(syncProgress.current / syncProgress.total) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Pending Items */}
          {pendingCount > 0 && (
            <div className="space-y-2 pt-2 border-t">
              <div className="flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span>{pendingCount} item(s) pendente(s)</span>
              </div>
              {!isOnline && (
                <p className="text-xs text-muted-foreground">
                  Os dados serão sincronizados automaticamente quando a conexão voltar.
                </p>
              )}
            </div>
          )}

          {/* Last Sync Result */}
          {lastSyncResult && pendingCount === 0 && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground pt-2 border-t">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span>
                Última sincronização: {lastSyncResult.synced} item(s)
                {lastSyncResult.failed > 0 && (
                  <span className="text-destructive"> ({lastSyncResult.failed} falha(s))</span>
                )}
              </span>
            </div>
          )}

          {/* Offline Mode Info */}
          {!isOnline && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3 text-sm">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-yellow-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-yellow-800 dark:text-yellow-200">
                    Modo Offline Ativo
                  </p>
                  <p className="text-yellow-700 dark:text-yellow-300 text-xs mt-1">
                    Vendas e comandas serão salvas localmente.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Offline Banner - Shows at top of page when offline
 */
export function OfflineBanner() {
  const { isOnline, pendingCount, isSyncing } = useOffline();

  if (isOnline && pendingCount === 0) {
    return null;
  }

  return (
    <div className={cn(
      'flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium transition-colors',
      !isOnline 
        ? 'bg-destructive text-destructive-foreground' 
        : isSyncing 
          ? 'bg-blue-600 text-white'
          : 'bg-yellow-500 text-yellow-950'
    )}>
      {!isOnline ? (
        <>
          <WifiOff className="h-4 w-4" />
          <span>Você está offline - Dados serão salvos localmente</span>
        </>
      ) : isSyncing ? (
        <>
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span>Sincronizando {pendingCount} item(s)...</span>
        </>
      ) : (
        <>
          <Clock className="h-4 w-4" />
          <span>{pendingCount} item(s) aguardando sincronização</span>
        </>
      )}
    </div>
  );
}

export default ConnectionIndicator;
